import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { VueHttpClientFactoryService } from '@vue/vueframework';
import { Observable, of } from 'rxjs';
import { AppDefaultsService } from '../../app-defaults.service';
import { TestCenterAffiliate } from '../types/test-center-affiliate.model';
import { TestCenterDetail } from '../types/test-center-detail.model';

@Injectable({
  providedIn: 'root'
})
export class TestCenterService {
  private httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  public constructor(
    private vueHttpClientFactoryService: VueHttpClientFactoryService,
    private appDefaultsService: AppDefaultsService
  ) { }

  /**
   * Checks whether the VUE ID attribute is needed for the given User.
   *
   * @return
   *    Boolean - True if the page needs to be shown.
   */
  public checkVueIdNeeded(): Observable<boolean> {
    const url = this.appDefaultsService.getTestCenterServicesRESTRoot() +
        '/api/v1/sitemaintenancesupport/checkVueId';
    return this.vueHttpClientFactoryService.getVueHttpClient().get(url, this.httpOptions);
  }

  /**
   * Associates the given VUE ID to the logged in user.
   *
   * @param vueId
   *      Student ID to be associated.
   *
   * @return boolean
   *      True if the update was successful.
   */
  public createVueId(vueId: number): Observable<boolean> {
    const url = this.appDefaultsService.getTestCenterServicesRESTRoot() +
        '/api/v1/sitemaintenancesupport/vueId/' + vueId + '/create';
    return this.vueHttpClientFactoryService.getVueHttpClient().post(url, null, this.httpOptions);
  }

  /**
   * Gets the list of affiliates the give site is allowed to administer
   *
   * @param siteId
   *      Site whose affiliate list is retrieved
   *
   * @param includeCurrentSite
   *      Includes the given site id in the list returned
   *
   * @return List of TestCenterAffiliate objects
   */
  public getAffiliates(siteId: number, includeCurrentSite: boolean): Observable<TestCenterAffiliate[]> {
    if (siteId) {
      const url = this.appDefaultsService.getTestCenterServicesRESTRoot() +
        '/api/v1/testcenters/' + siteId.toString() + '/affiliates?includeCurrentSite=' + includeCurrentSite;
      return this.vueHttpClientFactoryService.getVueHttpClient().get(url, this.httpOptions);
    }
    return of([]);
  }

  /**
   * Gets the details of the given site id
   *
   * @param siteId
   *      Site whose details are obtained
   *
   * @return List of TestCenterDetail objects
   */
  public getTestCenterDetails(siteId: string | number): Observable<TestCenterDetail[]> {
    if (siteId) {
      const url = this.appDefaultsService.getTestCenterServicesRESTRoot() +
        '/api/v1/testcenters?ids=' + siteId.toString();
      return this.vueHttpClientFactoryService.getVueHttpClient().get(url, this.httpOptions);
    }
    return of([]);
  }

  /**
   * Gets the VUE ID associated to the user.
   *
   * @return number
   *     VUE ID associated to the user
   */
  public getVueId(): Observable<number> {
    const url = this.appDefaultsService.getTestCenterServicesRESTRoot() +
        '/api/v1/sitemaintenancesupport/vueId';
    return this.vueHttpClientFactoryService.getVueHttpClient().get(url, this.httpOptions);
  }

  /**
   * Checks if the given VUE ID is a valid Student ID.
   *
   * @param vueId
   *     VUE ID to be validated
   * @return boolean
   *     True if it is valid
   */
  public validateVueId(vueId: number): Observable<boolean> {
    const url = this.appDefaultsService.getTestCenterServicesRESTRoot() +
        '/api/v1/sitemaintenancesupport/vueId/' + vueId + '/validate';
    return this.vueHttpClientFactoryService.getVueHttpClient().get(url, this.httpOptions);
  }
}
